import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useAppConfig } from './lib/appConfig';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Scripts from './pages/Scripts';
import LiveProduction from './pages/LiveProduction';
import Production from './pages/Production';
import AiProduction from './pages/AiProduction';
import Calendar from './pages/Calendar';
import Scheduler from './pages/Scheduler';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import AccountSettings from './pages/AccountSettings';
import EditQueue from './pages/EditQueue';
import ScriptShareView from './pages/ScriptShareView';
import ProductionAssistant from './pages/ProductionAssistant';
import Qual from './pages/Qual';
import TalentDashboard from './pages/TalentDashboard';
import TalentAvailabilityCalendar from './pages/TalentAvailabilityCalendar';
import TalentScriptView from './pages/TalentScriptView';
import TalentSetup from './pages/TalentSetup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VideoReviewPage from './pages/VideoReviewPage';
import ShareLinkViewer from './pages/ShareLinkViewer';
import FootageShareViewer from './pages/FootageShareViewer';
import AssetLibrary from './pages/AssetLibrary';
import CameraIngest from './pages/CameraIngest';
import RawFootage from './pages/RawFootage';
import Storage from './pages/Storage';
import TimeOff from './pages/TimeOff';
import './styles/globals.css';

// Thin wrapper: must be logged in, but does NOT render the sidebar
function AuthRequired({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading">Loading…</div>;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

// Smart default redirect — Talent-only users go to their portal; dual-role
// users (Talent + any staff role) default to the staff dashboard.
function DefaultRedirect() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isTalentOnly = roles.length === 1 && roles[0] === 'Talent';
  if (isTalentOnly) return <Navigate to="/talent" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  // AI Production is deployment-flagged; with the flag off the /ai route is
  // simply not mounted (the sidebar link is hidden in Sidebar.jsx).
  const { aiProductionEnabled } = useAppConfig();
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/share/script/:token" element={<ScriptShareView />} />
          <Route path="/talent-setup/:token" element={<TalentSetup />} />
          <Route path="/share/footage/:token" element={<FootageShareViewer />} />
          <Route path="/share/:token" element={<ShareLinkViewer />} />
          <Route path="/change-password" element={
            <AuthRequired><ChangePassword /></AuthRequired>
          } />
          <Route path="/review/:videoAssetId" element={
            <AuthRequired><VideoReviewPage /></AuthRequired>
          } />
          <Route element={<PrivateRoute />}>
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/scripts" element={<Scripts />} />
            <Route path="/qual" element={<Qual />} />
            <Route path="/live" element={<LiveProduction />} />
            <Route path="/production/:id" element={<Production />} />
            {aiProductionEnabled && <Route path="/ai" element={<AiProduction />} />}
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/edit-queue" element={<EditQueue />} />
            <Route path="/scheduler" element={<Scheduler />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/assistant" element={<ProductionAssistant />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/account" element={<AccountSettings />} />
            <Route path="/time-off" element={<TimeOff />} />
            <Route path="/assets" element={<AssetLibrary />} />
            <Route path="/footage" element={<RawFootage />} />
            <Route path="/footage/ingest" element={<CameraIngest />} />
            <Route path="/storage" element={<Storage />} />
            {/* Talent Portal */}
            <Route path="/talent" element={<TalentDashboard />} />
            <Route path="/talent/availability" element={<TalentAvailabilityCalendar />} />
            <Route path="/talent/scripts" element={<TalentScriptView />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
